import { NextAuthOptions } from 'next-auth';
import type { JWT } from 'next-auth/jwt';
import KeycloakProvider from 'next-auth/providers/keycloak';

import { getCurrentTimeInSeconds } from '../utils/Time';
import { PAGE } from './pages';
import { NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE } from './constants';
import { identifyUser } from './identifyUser';

import {
  accountSchema,
  jwtSchema,
  profileSchema,
  refreshTokenSchema,
} from '../schema/next-auth';
import { logger } from '@dist-marketplace-frontend/services';

const buildRefreshTokenRequest = (token: JWT) => {
  const details = {
    client_id: process.env.OIDC_CLIENT,
    client_secret: process.env.OIDC_CLIENT_SECRET,
    grant_type: 'refresh_token',
    refresh_token: token.refreshToken,
  };

  const formBody: string[] = [];

  Object.entries(details).forEach(
    ([key, value]: [string, string | undefined]) => {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value ?? '');
      formBody.push(encodedKey + '=' + encodedValue);
    },
  );

  const formData = formBody.join('&');

  const url = `${process.env.OIDC_URL}/protocol/openid-connect/token`;

  return { formData, url };
};

/**
 * Takes a token, and returns a new token with updated
 * `accessToken` and `accessTokenExpires`. If an error occurs,
 * returns the old token and an error property
 */
/**
 * @param  {JWT} token
 */
const refreshAccessToken = async (token: JWT) => {
  try {
    const { formData, url } = buildRefreshTokenRequest(token);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      },
      body: formData,
    });

    if (!response.ok) {
      logResponseError(response);

      return {
        ...token,
        error: NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE,
      };
    }

    const refreshedTokens = await response.json();

    const parsedRefreshedTokens = refreshTokenSchema.safeParse(refreshedTokens);

    if (!parsedRefreshedTokens.success) {
      logger.error({
        route: PAGE.AUTHENTICATION,
        message: 'Error parsing refresh tokens',
        error: parsedRefreshedTokens.error,
        userId: getUserIdFromToken(token),
      });

      return {
        ...token,
        error: NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE,
      };
    }

    const currentTimeInSeconds = getCurrentTimeInSeconds();

    /*
     * If the refresh token has expired, preventing the access token from being refreshed
     * and the session continuing, we add an error field to the token, triggering a logout
     */
    if (parsedRefreshedTokens.data.refresh_expires_in <= 0) {
      return {
        ...token,
        error: NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE,
      };
    }

    return {
      ...token,
      accessToken: parsedRefreshedTokens.data.access_token,
      expiresAt: currentTimeInSeconds + parsedRefreshedTokens.data.expires_in,
      refreshToken:
        parsedRefreshedTokens.data.refresh_token ?? token.refreshToken,
    };
  } catch (error) {
    logger.error({
      route: PAGE.AUTHENTICATION,
      message: 'Something went wrong attempting to refresh the tokens',
      error,
    });
    return {
      ...token,
      error: NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE,
    };
  }

  async function logResponseError(response: Response) {
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch (error) {
      try {
        responseBody = await response.text();
      } catch (error) {}
    }

    // This case mostly happens when the refresh token has expired
    if (
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'error' in responseBody &&
      responseBody.error === 'invalid_grant'
    ) {
      logger.info({
        route: PAGE.AUTHENTICATION,
        message: 'Could not refresh access token, got invalid grant response',
        response: responseBody,
        responseStatus: response.status,
        userId: getUserIdFromToken(token),
      });
    } else {
      logger.error({
        route: PAGE.AUTHENTICATION,
        message: 'Unknown error occurred while refreshing access token',
        response: responseBody,
        responseStatus: response.status,
        userId: getUserIdFromToken(token),
      });
    }
  }
};

const parsedMaxAge = process.env.SESSION_MAX_AGE_IN_SECONDS
  ? Number.parseInt(process.env.SESSION_MAX_AGE_IN_SECONDS, 10)
  : null;

export const nextAuthOptions: NextAuthOptions = {
  // Configure keycloak as the auth provider
  providers: [
    KeycloakProvider({
      clientId: process.env.OIDC_CLIENT || '',
      clientSecret: process.env.OIDC_CLIENT_SECRET || '',
      issuer: process.env.OIDC_URL,
      authorization: { params: { scope: process.env.OIDC_SCOPE || '' } },
    }),
  ],
  session: {
    strategy: 'jwt',
    /**
     * We set the maxAge to keep client sessions in sync with server sessions.
     *
     * If no value is provided we default to 31 days.
     *
     * Note that the parseInt return type may be a number despite the value being NaN
     */
    maxAge:
      parsedMaxAge && !Number.isNaN(parsedMaxAge)
        ? parsedMaxAge
        : 31 * 24 * 60 * 60, // 31 days
  },
  callbacks: {
    /**
     * This callback is called whenever a JSON Web Token is created (i.e. at sign in) or updated (i.e whenever a session is accessed in the client).
     * Its content is forwarded to the session callback, where we can control what should be returned to the client. Anything else will be kept from the front-end.
     *
     * For the initial login:
     *   - The token is a small set of basic fields like name, email that next-auth sets.
     *   - The account contains information about the provider being used and also extends TokenSet, which is different tokens returned by OAuth Providers.
     *   - The profile corresponds to the generated ID token on Keycloak, which includes whatever user information.
     *     We add the information we need to the token which is passed to the session and accessed on the client.
     */
    async jwt({ token, account, profile }) {
      const currentTimeInSeconds = getCurrentTimeInSeconds();

      const ACCESS_TOKEN_LIFESPAN_IN_SECONDS = 300;

      /**
       * This function is called whenever a new token needs to be created or refreshed.
       * Account and profile are returned on initial call (i.e. on sign-in) only.
       * For subsequent calls, the previous token is passed.
       */
      if (account && profile) {
        const parsedAccountParameter = accountSchema.safeParse(account);

        if (parsedAccountParameter.success) {
          /**
           * If the time on the user's device is not in sync with the time on the server,
           * refresh the access token immediately which will make the expiry time relative
           * the time on the user's device.
           */
          const expiryTime =
            typeof account?.expires_at === 'number' &&
            account.expires_at - currentTimeInSeconds <=
              ACCESS_TOKEN_LIFESPAN_IN_SECONDS &&
            account.expires_at - currentTimeInSeconds > 0
              ? account.expires_at
              : 0;
          /**
           * Since account, user, and profile are only available on sign-in,
           * we persist those values we want to reference via the session object
           * by assigning them as fields in the token passed to the session callback.
           */
          token.accessToken = parsedAccountParameter.data.access_token;
          token.refreshToken = parsedAccountParameter.data.refresh_token;
          token.idToken = parsedAccountParameter.data.id_token;
          token.expiresAt = expiryTime;
        } else {
          logger.error({
            route: PAGE.AUTHENTICATION,
            message: 'Error found in account parameter',
            error: parsedAccountParameter.error,
          });
        }

        token.picture = '';

        const parsedProfileParameter = profileSchema.safeParse(profile);

        if (parsedProfileParameter.success) {
          token.id = parsedProfileParameter.data.user_id;
        } else if (parsedProfileParameter.error) {
          const userWithErrorId = identifyUser({
            /**
             * Typescript assumes that user_id is a string if profile is defined,
             * but if there is a parsing error and user_id is in fact missing,
             * the value would actually be undefined
             */
            donedealUserId: profile.user_id ?? null,
            keycloakUserId: profile.sub ?? null,
          });

          logger.error({
            route: PAGE.AUTHENTICATION,
            message: `Profile parameters did not match schema at sign-in for ${userWithErrorId}`,
            error: parsedProfileParameter.error,
          });
        }
      }

      const timeRemainingToRefresh = Math.round(
        token.expiresAt - currentTimeInSeconds,
      );

      if (timeRemainingToRefresh > 0) {
        return token;
      }

      /**
       * Once the token has determined to have expired and is therefore no longer valid,
       * we attempt to refresh the access token and update the accessToken, refreshToken, expiresAt, and idToken fields.
       * The fields set with profile data such as id remain the same.
       */
      return refreshAccessToken(token);
    },
    async session({ session, token }) {
      /**
       * We parse the token here rather than the JWT callback to ensure that
       * the token provided includes the fields added in that JWT callback
       */
      const parsedToken = jwtSchema.safeParse(token);

      if (!parsedToken.success) {
        logger.error({
          route: PAGE.AUTHENTICATION,
          message: 'Error parsing token passed to session callback',
          error: parsedToken.error,
        });
        return session;
      }

      if (parsedToken.data.error) {
        session.error = parsedToken.data.error;
        return session;
      }

      session.accessToken = parsedToken.data.accessToken;
      session.idToken = parsedToken.data.idToken;
      session.expiresAt = parsedToken.data.expiresAt;
      session.user = {
        name: parsedToken.data.name,
        email: parsedToken.data.email,
        image: parsedToken.data.picture,
        id: parsedToken.data.id,
      };

      return session;
    },
  },
  pages: {
    signIn: '/signin',
  },
  debug: process.env.NEXTAUTH_DEBUG === 'true' ? true : false,
};

function getUserIdFromToken(token: JWT) {
  return identifyUser({
    keycloakUserId: token.sub ?? null,
    donedealUserId: typeof token.user_id === 'string' ? token.user_id : null,
  });
}
